// Admin UI preview — mock scenario data only (no API). Mirrors Account → Gumroad states.

import { GUMROAD_BUNDLE_ELIGIBILITY_PATH, GUMROAD_BUNDLE_UI_STATE } from './gumroadBundleUiConfig';

const DEFAULT_MARKETPLACE_DETAILS_PATH =
  '/marketplace/details?productId=6996dd38a9fec71d824770d3';

/** Selectable mock scenarios for admin Gumroad tab preview. */
export const GUMROAD_BUNDLE_ADMIN_PREVIEW_SCENARIOS = [
  { id: 'loading', label: 'Loading' },
  { id: 'error', label: 'Error — status load failed' },
  { id: 'not_configured', label: 'Not configured — no Gumroad token' },
  { id: 'already_entitled', label: 'Already entitled' },
  { id: 'has_promo', label: 'Has promo code' },
  { id: 'eligible_gumroad', label: 'Eligible — Gumroad purchaser email' },
  { id: 'eligible_transfer', label: 'Eligible — transfer recipient' },
  { id: 'eligible_issuance_pending', label: 'Eligible — Stripe coupon not configured' },
  { id: 'not_eligible', label: 'Not eligible — no purchase match' },
  {
    id: 'not_eligible_transfer_pending',
    label: 'Not eligible — transfer pending finalize',
  },
];

/**
 * @param {string} [liveUiState]
 * @returns {string}
 */
export function resolveDefaultAdminPreviewScenarioId(liveUiState) {
  const match = GUMROAD_BUNDLE_ADMIN_PREVIEW_SCENARIOS.find((s) => {
    if (s.id === 'eligible_gumroad' && liveUiState === GUMROAD_BUNDLE_UI_STATE.ELIGIBLE) {
      return true;
    }
    if (s.id === liveUiState) return true;
    return false;
  });
  return match?.id ?? 'already_entitled';
}

/**
 * @param {string} scenarioId
 * @param {{ marketplaceDetailsPath?: string }} [options]
 * @returns {{ uiState: string, status: object | null, errorMessage: string | null }}
 */
export function buildGumroadBundleAdminPreviewDisplay(scenarioId, options = {}) {
  const marketplaceDetailsPath =
    options.marketplaceDetailsPath || DEFAULT_MARKETPLACE_DETAILS_PATH;

  const issuanceReady = {
    gumroadConfigured: true,
    bundlePromoIssuanceConfigured: true,
    configured: true,
    canRequestPromo: true,
    marketplaceDetailsPath,
  };

  switch (scenarioId) {
    case 'loading':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.LOADING,
        status: null,
        errorMessage: null,
      };
    case 'error':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.ERROR,
        status: null,
        errorMessage: 'Could not load bundle promo status.',
      };
    case 'not_configured':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.NOT_CONFIGURED,
        status: {
          gumroadConfigured: false,
          bundlePromoIssuanceConfigured: false,
          configured: false,
          canRequestPromo: false,
          marketplaceDetailsPath,
        },
        errorMessage: null,
      };
    case 'already_entitled':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.ALREADY_ENTITLED,
        status: { ...issuanceReady, code: 'already_has_local_revolt' },
        errorMessage: null,
      };
    case 'has_promo':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.HAS_PROMO,
        status: {
          ...issuanceReady,
          promo: {
            promotionCode: 'REVOLT-MOCK-BUNDLE',
            createdAt: '2026-06-20T12:00:00.000Z',
          },
        },
        errorMessage: null,
      };
    case 'eligible_gumroad':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.ELIGIBLE,
        status: {
          ...issuanceReady,
          eligibilityPath: GUMROAD_BUNDLE_ELIGIBILITY_PATH.GUMROAD_EMAIL,
        },
        errorMessage: null,
      };
    case 'eligible_transfer':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.ELIGIBLE,
        status: {
          ...issuanceReady,
          eligibilityPath: GUMROAD_BUNDLE_ELIGIBILITY_PATH.TRANSFER_RECIPIENT,
        },
        errorMessage: null,
      };
    case 'eligible_issuance_pending':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.ELIGIBLE,
        status: {
          ...issuanceReady,
          bundlePromoIssuanceConfigured: false,
          canRequestPromo: false,
          eligibilityPath: GUMROAD_BUNDLE_ELIGIBILITY_PATH.GUMROAD_EMAIL,
        },
        errorMessage: null,
      };
    case 'not_eligible':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.NOT_ELIGIBLE,
        status: { ...issuanceReady, code: 'not_gumroad_purchaser' },
        errorMessage: null,
      };
    case 'not_eligible_transfer_pending':
      return {
        uiState: GUMROAD_BUNDLE_UI_STATE.NOT_ELIGIBLE,
        status: { ...issuanceReady, code: 'transfer_pending_admin_finalize' },
        errorMessage: null,
      };
    default:
      return buildGumroadBundleAdminPreviewDisplay('already_entitled', options);
  }
}
